import React, { useCallback, useEffect, useMemo } from 'react';
import { Select, MenuItem, SelectProps } from '@mui/material';
import { decode } from '../../Services/DecodeToken';
import {
  EmptyStateItem,
  SEARCHABLE_SELECT_WIDTH,
  SearchableSelectSearchBar,
  defaultOptionSx,
  selectFocusSx,
  selectedOptionSx,
  useSearchableSelect,
} from './SearchableSelectShared';

interface Location {
  OVID: string | number;
  name: string;
}

interface LocationSelectProps extends Omit<SelectProps, 'value' | 'onChange'> {
  ovs: Location[];
  selectedOvs: any;
  onChange: (value: any) => void;
  defaultName?: string;
  disabled?: boolean;
  setDefault?: boolean;
  searchable?: boolean;
}

export const LocationSelect: React.FC<LocationSelectProps> = ({
  ovs,
  selectedOvs,
  onChange,
  defaultName = 'All Locations',
  disabled = false,
  setDefault = false,
  searchable = true,
  ...props
}) => {
  const token: any = decode();

  // Always A-Z by name, whatever order the API returns, so new locations slot in.
  const sortedOvs = useMemo(
    () => [...(ovs ?? [])].sort((a, b) =>
      String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { sensitivity: 'base', numeric: true }),
    ),
    [ovs],
  );

  const handleSelect = useCallback((value: string | number) => {
    onChange(value === "" ? "" : value);
  }, [onChange]);

  const {
    search,
    setSearch,
    open,
    handleOpen,
    handleClose,
    searchInputRef,
    selectRef,
    menuProps,
    handleSearchKeyDown: baseSearchKeyDown,
  } = useSearchableSelect({
    searchable,
    onEnterSelect: () => {
      const query = search.trim().toLowerCase();
      const matches = query
        ? sortedOvs.filter((item) => item.name.toLowerCase().includes(query))
        : sortedOvs;
      if (matches.length > 0) {
        handleSelect(matches[0].OVID);
        handleClose();
      }
    },
  });

  const filteredResults = useMemo(() => {
    if (!searchable || !search.trim()) return sortedOvs;
    const query = search.trim().toLowerCase();
    return sortedOvs.filter((item) => item.name.toLowerCase().includes(query));
  }, [sortedOvs, search, searchable]);

  useEffect(() => {
    const needsDefault = selectedOvs === undefined || selectedOvs === null;
    if (!needsDefault || ovs.length === 0) return;

    if (setDefault) {
      onChange("");
    } else if (token.roles?.id === 1) {
      onChange("");
    }
  }, [token.roles?.id, selectedOvs, ovs, setDefault, onChange]);

  const resultLabel = search.trim()
    ? filteredResults.length === 0
      ? 'No matches'
      : `${filteredResults.length} match${filteredResults.length === 1 ? '' : 'es'}`
    : `${ovs.length} location${ovs.length === 1 ? '' : 's'}`;

  const onSelectChange = (value: string | number) => {
    handleSelect(value);
    handleClose();
  };

  return (
    <Select
      {...props}
      ref={selectRef}
      disabled={disabled}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      size="small"
      value={selectedOvs || ""}
      onChange={(e) => onSelectChange(e.target.value as string | number)}
      displayEmpty
      renderValue={() => {
        if (!selectedOvs || selectedOvs === "") return defaultName;
        const selected = ovs.find((item) => item.OVID == selectedOvs);
        return selected?.name ?? 'Select Location';
      }}
      MenuProps={menuProps}
      sx={{
        minWidth: { xs: 0, sm: SEARCHABLE_SELECT_WIDTH },
        width: { xs: '100%', sm: 'auto' },
        maxWidth: '100%',
        ...selectFocusSx,
        ...props.sx,
      }}
    >
      {searchable && (
        <SearchableSelectSearchBar
          placeholder="Search locations..."
          search={search}
          onSearchChange={setSearch}
          onKeyDown={baseSearchKeyDown}
          resultLabel={resultLabel}
          searchInputRef={searchInputRef}
        />
      )}

      <MenuItem value="" sx={defaultOptionSx}>
        <em>{defaultName}</em>
      </MenuItem>

      {filteredResults.length > 0 ? (
        filteredResults.map((item) => (
          <MenuItem
            key={item.OVID}
            value={item.OVID}
            selected={item.OVID == selectedOvs}
            sx={selectedOptionSx}
          >
            {item.name}
          </MenuItem>
        ))
      ) : searchable && search.trim() ? (
        <EmptyStateItem disabled value="__no_results__">
          No locations found
        </EmptyStateItem>
      ) : null}
    </Select>
  );
};
